export type SupportedLocale = 'en' | 'ko' | 'vi' | 'zh-Hant'

export const SUPPORTED_LOCALES: ReadonlyArray<SupportedLocale> = ['en', 'ko', 'vi', 'zh-Hant']

export const SUPPORTED_LOCALE_OPTIONS: ReadonlyArray<{ code: SupportedLocale; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'ko', label: '한국어' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'zh-Hant', label: '繁體中文' },
]

// NOTE: content/output language (axis 3) options are NOT derived from the UI
// locales — they are the TTS-supported set and live in `contentLanguages.ts`.
// Axis 3 carries a single `zh` (中文) because ElevenLabs speaks Mandarin without
// a script distinction; script only matters for WRITTEN UI copy, which is why
// the UI locale is `zh-Hant` and the content language stays `zh`.

const SUPPORTED_LOCALE_SET: ReadonlyMap<string, SupportedLocale> = new Map(
  SUPPORTED_LOCALES.map((locale) => [locale.toLowerCase(), locale]),
)

// Regions that write Traditional Chinese. A browser advertising `zh-TW` is
// asking for Traditional script via a region proxy, so it resolves to `zh-Hant`.
// `zh-CN` / `zh-SG` (Simplified) deliberately do NOT appear: they fall through
// to the base-subtag step, find no bare `zh` locale, and degrade to English
// rather than being served the wrong script.
const TRADITIONAL_CHINESE_REGIONS: ReadonlySet<string> = new Set(['tw', 'hk', 'mo'])

export function isSupportedLocale(lang: string): lang is SupportedLocale {
  return SUPPORTED_LOCALE_SET.has(lang.toLowerCase())
}

/**
 * Resolve an arbitrary language tag to a supported UI locale, or `null`.
 *
 * Resolution order — the first two steps exist because a locale can carry a
 * SCRIPT subtag. The original implementation was `lang.split('-')[0]`, which
 * collapsed every tag to its base subtag; under that rule `zh-Hant` resolved to
 * `zh`, matched nothing, and returned `null` — so a user who picked Traditional
 * Chinese in the profile picker would not see their own choice reflected back.
 *
 *   1. Exact match on the full tag (case-insensitive), so `zh-hant` -> `zh-Hant`.
 *   2. Traditional-Chinese region proxies (`zh-TW`/`zh-HK`/`zh-MO`) -> `zh-Hant`.
 *   3. Base subtag, so `ko-KR` -> `ko` (unchanged behavior for en/ko/vi).
 *
 * Returns the canonically-cased code so callers can compare against
 * `SUPPORTED_LOCALES` with `===`.
 */
export function normalizeLocale(lang: string | null | undefined): SupportedLocale | null {
  if (!lang || typeof lang !== 'string') return null
  const raw = lang.trim().toLowerCase().replace(/_/g, '-')
  if (!raw) return null

  const exact = SUPPORTED_LOCALE_SET.get(raw)
  if (exact) return exact

  const [base, ...rest] = raw.split('-')
  // An EXPLICIT script subtag always wins over the region proxy. `zh-Hans-HK` is
  // a real tag (Simplified as written in Hong Kong); treating its `hk` as a
  // Traditional proxy would hand a reader the exact script they said they did
  // not want — strictly worse than degrading to English, which is the whole
  // reason `zh-CN` is not in the proxy set.
  if (base === 'zh' && rest.includes('hans')) {
    return SUPPORTED_LOCALE_SET.get('zh-hans') ?? null
  }
  if (base === 'zh' && rest.some((part) => TRADITIONAL_CHINESE_REGIONS.has(part))) {
    return 'zh-Hant'
  }

  return SUPPORTED_LOCALE_SET.get(base) ?? null
}
